import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, X, Paperclip, FileCheck2, FileWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ tipo: "entrada", categoria: "Receitas Diversas", subcategoria: "", descricao: "", valor: 0, data: new Date().toISOString().slice(0, 10), cliente_id: "", vendedor_id: "", nota_fiscal: null });
const ALLOWED_NF_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

export default function Cash() {
  const [data, setData] = useState({ items: [], entradas: 0, saidas: 0, saldo: 0 });
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [tipo, setTipo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [nfFilter, setNfFilter] = useState("");
  const [uploadingNF, setUploadingNF] = useState(false);
  const nfRef = useRef(null);

  const load = async () => {
    const params = {};
    if (tipo) params.tipo = tipo;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    const [c, cl, sl] = await Promise.all([
      api.get("/cash", { params }),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/sellers").catch(() => ({ data: [] })),
    ]);
    setData(c.data); setClients(cl.data); setSellers(sl.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tipo, from, to]);

  const cName = (id) => clients.find(c => c.id === id)?.nome || "—";
  const sName = (id) => sellers.find(s => s.id === id)?.nome || "—";
  const refName = (c) => c.cliente_id ? cName(c.cliente_id) : (c.vendedor_id ? sName(c.vendedor_id) : "—");

  const sourceLabel = (st) => {
    if (st === "commission") return "comissão paga";
    if (st === "commission_manual") return "pagamento manual de comissão";
    if (st === "bonus") return "bonificação paga";
    if (st === "payable") return "conta a pagar quitada";
    if (st === "payment") return "recebimento de cliente";
    return null;
  };

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty(), ...c }); setOpen(true); };

  const onPickNF = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_NF_TYPES.includes(f.type)) { toast.error("Formato inválido — use PDF, JPG, JPEG ou PNG"); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error("Arquivo acima de 20MB"); return; }
    setUploadingNF(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error("upload");
      const d = await r.json();
      setForm(prev => ({ ...prev, nota_fiscal: { id: d.id, filename: f.name } }));
      toast.success("Nota fiscal anexada");
    } catch { toast.error("Falha no upload"); }
    finally { setUploadingNF(false); if (nfRef.current) nfRef.current.value = ""; }
  };

  const save = async () => {
    try {
      const payload = { ...form, valor: parseFloat(form.valor) };
      if (editing) await api.put(`/cash/${editing.id}`, payload);
      else await api.post("/cash", payload);
      toast.success(editing ? "Atualizado" : "Lançamento criado");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (c) => {
    const src = sourceLabel(c.source_type);
    const warn = src
      ? `Este lançamento foi gerado por ${src}. Ao excluir, o registro voltará a estado "pendente" na origem.\n\nConfirma a exclusão?`
      : `Excluir lançamento de ${fmtBRL(c.valor)}?`;
    if (!window.confirm(warn)) return;
    try {
      const { data: res } = await api.delete(`/cash/${c.id}`);
      if (res.reverted) toast.success(`Excluído. ${res.reverted} voltou a pendente.`);
      else toast.success("Excluído");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const clearFilters = () => { setTipo(""); setFrom(""); setTo(""); setNfFilter(""); };
  const hasFilters = tipo || from || to || nfFilter;
  const visibleItems = data.items.filter(c => {
    if (nfFilter === "com") return !!c.nota_fiscal;
    if (nfFilter === "sem") return !c.nota_fiscal;
    return true;
  });

  return (
    <Layout title="Caixa" subtitle="Livro caixa - entradas e saídas">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Entradas</p><p className="kpi-num text-2xl mt-2 text-emerald-700">{fmtBRL(data.entradas)}</p></Card>
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Saídas</p><p className="kpi-num text-2xl mt-2 text-red-700">{fmtBRL(data.saidas)}</p></Card>
        <Card className="p-4 rounded-sm"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Saldo</p><p className="kpi-num text-2xl mt-2">{fmtBRL(data.saldo)}</p></Card>
      </div>
      <Card className="p-4 rounded-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs font-semibold">Tipo</Label>
            <Select value={tipo || "all"} onValueChange={(v) => setTipo(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cash-filter-tipo" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="entrada">Entradas</SelectItem><SelectItem value="saida">Saídas</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">Data de</Label><Input data-testid="cash-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1"/></div>
          <div><Label className="text-xs font-semibold">Data até</Label><Input data-testid="cash-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1"/></div>
          <div>
            <Label className="text-xs font-semibold">Nota fiscal</Label>
            <Select value={nfFilter || "all"} onValueChange={(v) => setNfFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cash-filter-nf" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="com">Com NF</SelectItem><SelectItem value="sem">Sem NF</SelectItem></SelectContent>
            </Select>
          </div>
          <div>{hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="cash-filter-clear" className="w-full"><X className="w-4 h-4 mr-1"/>Limpar</Button>}</div>
          <div className="text-right">
            <Button data-testid="cash-new" onClick={openNew}><Plus className="w-4 h-4 mr-1"/>Novo Lançamento</Button>
          </div>
        </div>
      </Card>
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Categoria</TableHead>
            <TableHead>Cliente / Vendedor</TableHead><TableHead>Descrição</TableHead>
            <TableHead>Usuário</TableHead><TableHead>NF</TableHead><TableHead>Valor</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>{visibleItems.map(c => (
            <TableRow key={c.id} data-testid={`cash-row-${c.id}`}>
              <TableCell>{fmtDate(c.data)}</TableCell>
              <TableCell><Badge variant="outline" className={c.tipo === "entrada" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}>{c.tipo}</Badge></TableCell>
              <TableCell>{c.categoria}</TableCell>
              <TableCell className="text-xs">{refName(c)}</TableCell>
              <TableCell className="text-xs">{c.descricao}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{c.user_name}</TableCell>
              <TableCell>
                {c.nota_fiscal ? (
                  <a href={`${API}/files/${c.nota_fiscal.id}/download`} target="_blank" rel="noreferrer" data-testid={`cash-nf-view-${c.id}`}
                     className="inline-flex items-center gap-1 text-[10px] text-emerald-700 hover:underline">
                    <FileCheck2 className="w-3.5 h-3.5"/>Ver NF
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-red-600" data-testid={`cash-nf-missing-${c.id}`}>
                    <FileWarning className="w-3.5 h-3.5"/>Sem NF
                  </span>
                )}
              </TableCell>
              <TableCell className={`font-semibold ${c.tipo === "entrada" ? "text-emerald-700" : "text-red-700"}`}>{c.tipo === "entrada" ? "+" : "-"}{fmtBRL(c.valor)}</TableCell>
              <TableCell className="text-right">
                <Button data-testid={`cash-edit-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="w-4 h-4"/></Button>
                <Button data-testid={`cash-delete-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c)}><Trash2 className="w-4 h-4"/></Button>
              </TableCell>
            </TableRow>
          ))}{visibleItems.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Sem lançamentos</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Lançamento" : "Novo Lançamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                <SelectTrigger className="mt-1" data-testid="cash-tipo"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="entrada">Entrada</SelectItem><SelectItem value="saida">Saída</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Categoria</Label><Input data-testid="cash-categoria" value={form.categoria} onChange={(e) => setForm({...form, categoria: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Descrição</Label><Input value={form.descricao || ""} onChange={(e) => setForm({...form, descricao: e.target.value})} className="mt-1"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Valor</Label><Input data-testid="cash-valor" type="number" value={form.valor} onChange={(e) => setForm({...form, valor: e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs font-semibold">Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({...form, data: e.target.value})} className="mt-1"/></div>
            </div>
            {clients.length > 0 && (
              <div>
                <Label className="text-xs font-semibold">Cliente (opcional)</Label>
                <Select value={form.cliente_id || "none"} onValueChange={(v) => setForm({...form, cliente_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Sem cliente —</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="p-3 border border-border rounded-sm bg-muted/20">
              <Label className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--primary))] flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5"/>Nota Fiscal
              </Label>
              {form.nota_fiscal ? (
                <div className="mt-2 flex items-center gap-2 p-2 bg-secondary border border-border rounded-sm text-xs">
                  <FileCheck2 className="w-4 h-4 text-emerald-600" />
                  <span className="flex-1 truncate">{form.nota_fiscal.filename}</span>
                  <a href={`${API}/files/${form.nota_fiscal.id}/download`} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline text-[10px]">abrir</a>
                  <button onClick={() => setForm(f => ({...f, nota_fiscal: null}))} className="hover:text-red-600" data-testid="cash-nf-remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="mt-2">
                  <input ref={nfRef} type="file" accept="application/pdf,image/jpeg,image/jpg,image/png" hidden onChange={onPickNF} data-testid="cash-nf-input" />
                  <Button type="button" size="sm" variant="outline" onClick={() => nfRef.current?.click()} disabled={uploadingNF} data-testid="cash-nf-upload">
                    {uploadingNF ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Paperclip className="w-3.5 h-3.5 mr-1"/>}
                    Anexar nota fiscal (PDF, JPG, JPEG, PNG)
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="cash-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
